import React, { useState, useEffect } from 'react';
import { Card, Button, Space, message, Typography, Divider, Alert, Tag, Descriptions } from 'antd';
import { ApiOutlined, CheckCircleOutlined, CloseCircleOutlined, KeyOutlined, UserOutlined } from '@ant-design/icons';
import type { LoginRequest } from '../types/user';
import permissionApiAdapter from '../api/permissionApiAdapter';
import { permissionApiV2 } from '../api/userApiV2';
import { authApi, userApi } from '../api/userApi';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const TestPermissionV2: React.FC = () => {
  const [testResults, setTestResults] = useState<any[]>([]);
  const [testing, setTesting] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [authInfo, setAuthInfo] = useState<{
    token: string | null;
    refreshToken: string | null;
    user: any;
    tokenValid: boolean | null;
  }>({
    token: null,
    refreshToken: null,
    user: null,
    tokenValid: null
  });

  useEffect(() => {
    refreshAuthInfo();
  }, []);

  const refreshAuthInfo = () => {
    // 獲取當前的認證資訊
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    let user = null;
    
    try {
      user = userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      console.error('Failed to parse user data:', e);
    }

    setAuthInfo({ token, refreshToken, user, tokenValid: null });
    
    // 如果有 token，驗證它是否有效
    if (token) {
      validateToken(token);
    }
  };

  const addResult = (test: string, success: boolean, message: string, data?: any) => {
    setTestResults(prev => [...prev, { test, success, message, data, timestamp: new Date() }]);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const validateToken = async (token: string) => {
    try {
      // 嘗試解析 JWT token
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        const exp = payload.exp * 1000; // 轉換為毫秒
        const isExpired = Date.now() > exp;
        
        if (isExpired) {
          setAuthInfo(prev => ({ ...prev, tokenValid: false }));
          addResult('Token 驗證', false, 'Token 已過期', { expiredAt: new Date(exp).toLocaleString() });
        } else {
          setAuthInfo(prev => ({ ...prev, tokenValid: true }));
          addResult('Token 驗證', true, `Token 有效，將於 ${new Date(exp).toLocaleString()} 過期`, payload);
        }
      }
    } catch (e) {
      console.error('Failed to parse token:', e);
      setAuthInfo(prev => ({ ...prev, tokenValid: false }));
      addResult('Token 驗證', false, 'Token 格式無效');
    }
  };

  const testManualAuth = async () => {
    setLoginLoading(true);
    clearResults();
    
    try {
      // 測試登入
      addResult('手動登入測試', true, '嘗試使用測試帳號登入...');
      
      const loginData: LoginRequest = {
        userId: 'admin', // 注意：使用 userId 而不是 username
        password: 'admin', // 使用正確的密碼
        dbType: 'A001' // 添加必需的 dbType 欄位
      };
      
      addResult('登入請求', true, '發送登入請求...', { endpoint: '/api/v1/auth/login', data: { userId: loginData.userId } });
      
      const response = await authApi.login(loginData);
      
      if (response.data.token) {
        // 儲存 token
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        addResult('手動登入測試', true, '登入成功！', response.data);
        
        // 刷新認證資訊
        refreshAuthInfo();
        
        message.success('登入成功！現在可以測試 V2 API');
      } else {
        addResult('手動登入測試', false, '登入失敗：沒有收到 token');
      }
    } catch (error: any) {
      addResult('手動登入測試', false, `登入失敗：${error.message}`, error);
      message.error('登入失敗');
    } finally {
      setLoginLoading(false);
    }
  };

  const debugRequest = async () => {
    clearResults();
    
    try {
      // 直接使用 axios 發送請求，看看具體的請求和回應
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      addResult('調試請求', true, '準備發送測試請求...');
      
      // 先測試不需要權限的端點
      const currentUserConfig = {
        method: 'get',
        url: `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/v2/users/current/permissions`,
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };
      
      addResult('測試當前用戶權限端點', true, '請求配置', currentUserConfig);
      
      try {
        const currentUserResponse = await axios(currentUserConfig);
        addResult('當前用戶權限', true, '成功獲取！', currentUserResponse.data);
      } catch (error: any) {
        if (error.response) {
          addResult('當前用戶權限', false, `錯誤 ${error.response.status}: ${error.response.statusText}`, {
            status: error.response.status,
            data: error.response.data
          });
        }
      }
      
      // 再測試需要權限的端點
      const groupsConfig = {
        method: 'get',
        url: `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/v2/permission-groups`,
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        params: { page: 1, limit: 10 }
      };
      
      addResult('測試權限群組端點', true, '請求配置', groupsConfig);
      
      const groupsResponse = await axios(groupsConfig);
      addResult('權限群組列表', true, '成功獲取！', groupsResponse.data);
    } catch (error: any) {
      if (error.response) {
        addResult('調試請求', false, `錯誤 ${error.response.status}: ${error.response.statusText}`, {
          status: error.response.status,
          headers: error.response.headers,
          data: error.response.data,
          config: {
            url: error.config.url,
            headers: error.config.headers
          }
        });
      } else {
        addResult('調試請求', false, '網路錯誤', error);
      }
    }
  };

  const testV2APIs = async () => {
    setTesting(true);
    clearResults();
    
    try {
      // Test 0: Get Current User Permissions (doesn't require permission check)
      try {
        addResult('獲取當前用戶權限', true, '開始測試...');
        const currentPerms = await permissionApiV2.getCurrentUserPermissions();
        addResult('獲取當前用戶權限', true, `成功！用戶: ${currentPerms.data.userId}`, currentPerms.data);
      } catch (error: any) {
        addResult('獲取當前用戶權限', false, `錯誤 ${error.response?.status || ''}: ${error.response?.data?.error?.message || error.message}`, error.response?.data || error);
      }

      // Test 1: Get Permission Groups
      try {
        addResult('獲取權限群組列表', true, '開始測試...');
        const groups = await permissionApiV2.getPermissionGroups({ page: 1, limit: 10 });
        addResult('獲取權限群組列表', true, `成功！獲取到 ${groups.data.items.length} 個群組`, groups.data);
      } catch (error: any) {
        addResult('獲取權限群組列表', false, `錯誤 ${error.response?.status || ''}: ${error.response?.data?.error?.message || error.message}`, error.response?.data || error);
      }

      // Test 2: Get Programs
      try {
        addResult('獲取程式列表', true, '開始測試...');
        const programs = await permissionApiV2.getPrograms({ page: 1, limit: 10 });
        addResult('獲取程式列表', true, `成功！獲取到 ${programs.data.items.length} 個程式`, programs.data);
      } catch (error: any) {
        addResult('獲取程式列表', false, `錯誤 ${error.response?.status || ''}: ${error.response?.data?.error?.message || error.message}`, error.response?.data || error);
      }

      // Test 3: Get Program Tree
      try {
        addResult('獲取程式樹狀結構', true, '開始測試...');
        const tree = await permissionApiV2.getProgramTree();
        addResult('獲取程式樹狀結構', true, `成功！獲取到樹狀結構`, tree.data);
      } catch (error: any) {
        addResult('獲取程式樹狀結構', false, `錯誤 ${error.response?.status || ''}: ${error.response?.data?.error?.message || error.message}`, error.response?.data || error);
      }

      // Test 4: Check Permission
      try {
        addResult('檢查權限', true, '開始測試...');
        const check = await permissionApiV2.checkPermission('admin', 1, 'query');
        addResult('檢查權限', true, `權限檢查結果: ${check.data.hasPermission ? '有權限' : '無權限'}`, check.data);
      } catch (error: any) {
        addResult('檢查權限', false, `錯誤 ${error.response?.status || ''}: ${error.response?.data?.error?.message || error.message}`, error.response?.data || error);
      }

      message.success('API 測試完成！');
    } catch (error) {
      message.error('測試過程中發生錯誤');
    } finally {
      setTesting(false);
    }
  };

  const testAdapter = async () => {
    setTesting(true);
    clearResults();
    
    try {
      // Test Adapter
      addResult('測試 API 適配器', true, '開始測試...');
      const groups = await permissionApiAdapter.getPermissionGroups({ page: 1, limit: 5 });
      addResult('測試 API 適配器', true, `適配器工作正常！`, groups.data);
      
      message.success('適配器測試完成！');
    } catch (error: any) {
      addResult('測試 API 適配器', false, error.message || '失敗', error);
      message.error('適配器測試失敗');
    } finally {
      setTesting(false);
    }
  };

  const testV1API = async () => {
    setTesting(true);
    clearResults();
    
    try {
      // Test V1 API to ensure basic auth is working
      addResult('測試 V1 API', true, '開始測試...');
      
      // Test 1: Get current user info
      try {
        addResult('獲取當前用戶資訊 (V1)', true, '測試中...');
        const userInfo = await authApi.getProfile();
        addResult('獲取當前用戶資訊 (V1)', true, `成功！用戶: ${userInfo.data.userId}`, userInfo.data);
      } catch (error: any) {
        addResult('獲取當前用戶資訊 (V1)', false, `錯誤 ${error.response?.status || ''}: ${error.response?.data?.message || error.message}`, error.response?.data || error);
      }
      
      // Test 2: Get users list
      try {
        addResult('獲取用戶列表 (V1)', true, '測試中...');
        const users = await userApi.getUsers({ page: 1, limit: 5 });
        addResult('獲取用戶列表 (V1)', true, `成功！獲取到 ${users.data.items?.length || 0} 個用戶`, { count: users.data.items?.length });
      } catch (error: any) {
        addResult('獲取用戶列表 (V1)', false, `錯誤 ${error.response?.status || ''}: ${error.response?.data?.message || error.message}`, error.response?.data || error);
      }
      
      message.success('V1 API 測試完成！');
    } catch (error) {
      message.error('V1 API 測試失敗');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={3}>
          <ApiOutlined /> 權限系統 V2 API 測試
        </Title>
        <Paragraph>
          這個頁面用於測試新的 V2 權限 API 是否正常工作。
        </Paragraph>
        
        {/* 顯示認證資訊 */}
        <Card 
          title={<><KeyOutlined /> 認證資訊</>} 
          size="small" 
          style={{ marginBottom: 24 }}
          type="inner"
        >
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Token 狀態">
              {authInfo.token ? (
                <Space>
                  <Tag color="success">已設定</Tag>
                  {authInfo.tokenValid !== null && (
                    <Tag color={authInfo.tokenValid ? 'green' : 'red'}>
                      {authInfo.tokenValid ? '有效' : '無效/過期'}
                    </Tag>
                  )}
                </Space>
              ) : (
                <Tag color="error">未設定</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Token 預覽">
              {authInfo.token ? (
                <Text code copyable ellipsis style={{ maxWidth: 400 }}>
                  {authInfo.token.substring(0, 20)}...
                </Text>
              ) : (
                <Text type="secondary">無</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Refresh Token">
              {authInfo.refreshToken ? (
                <Tag color="success">已設定</Tag>
              ) : (
                <Tag color="warning">未設定</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="當前用戶">
              {authInfo.user ? (
                <Space>
                  <UserOutlined />
                  <Text>{authInfo.user.userId || authInfo.user.username || '未知'}</Text>
                </Space>
              ) : (
                <Text type="secondary">未登入</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
          
          {!authInfo.token && (
            <Alert
              message="未檢測到認證 Token"
              description="請先登入系統，或檢查 localStorage/sessionStorage 中是否有 'token' 欄位。"
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Card>

        <Space wrap>
          <Button 
            type="primary" 
            onClick={testV2APIs}
            loading={testing}
            icon={<ApiOutlined />}
            disabled={!authInfo.token}
          >
            測試 V2 API
          </Button>
          <Button 
            onClick={testV1API}
            loading={testing}
            disabled={!authInfo.token}
          >
            測試 V1 API
          </Button>
          <Button 
            onClick={testAdapter}
            loading={testing}
            disabled={!authInfo.token}
          >
            測試適配器
          </Button>
          <Button
            onClick={debugRequest}
            loading={testing}
            disabled={!authInfo.token}
          >
            調試請求
          </Button>
          <Button
            onClick={testManualAuth}
            loading={loginLoading}
            type="dashed"
            danger={!authInfo.token}
          >
            {authInfo.token ? '重新登入' : '手動登入測試'}
          </Button>
          <Button 
            onClick={() => {
              refreshAuthInfo();
              message.info('已刷新認證資訊');
            }}
          >
            刷新狀態
          </Button>
          <Button 
            onClick={clearResults}
            disabled={testing || loginLoading}
          >
            清除結果
          </Button>
        </Space>

        <Divider />

        <div style={{ marginTop: 24 }}>
          <Title level={4}>測試結果</Title>
          {testResults.length === 0 ? (
            <Alert message="尚未執行測試" type="info" />
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {testResults.map((result, index) => (
                <Card 
                  key={index} 
                  size="small"
                  style={{ 
                    borderColor: result.success ? '#52c41a' : '#ff4d4f',
                    backgroundColor: result.success ? '#f6ffed' : '#fff2f0'
                  }}
                >
                  <Space>
                    {result.success ? (
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                    )}
                    <div>
                      <Text strong>{result.test}</Text>
                      <br />
                      <Text type="secondary">{result.message}</Text>
                      {result.data && (
                        <details style={{ marginTop: 8 }}>
                          <summary style={{ cursor: 'pointer' }}>查看詳細資料</summary>
                          <pre style={{ 
                            marginTop: 8, 
                            padding: 8, 
                            backgroundColor: '#f5f5f5',
                            borderRadius: 4,
                            overflow: 'auto',
                            maxHeight: 200
                          }}>
                            {JSON.stringify(result.data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </Space>
                </Card>
              ))}
            </Space>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TestPermissionV2;